/****************************************************
 * Module Name:  stockExport.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState } from "react";
import Select from "react-select";
import Constants from "../../utility/constants";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import { Box, Button, Container, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import renderCellExpand from "../common/dataGridCellExpand";
import Utility from "../../utility/utility";
import { useEffect } from "react";
import StoneDetailModal from "../common/stoneDetailModal";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import { styled, alpha } from "@mui/material/styles";
import NoPermission from "../common/noPermission";

import StockTallyProxy from "../../utility/proxy/manager/stockTallyProxy";
import TabMenuSnackbar from "../common/tabMenuSnackbar";
import LocationProxy from "../../utility/proxy/manager/locationProxy";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import colourchart from '../../images/ColorScheme.png'
import PageTitle from "../common/pageTitle";

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
    "& .super-app-theme--AVAILABLE": {
        color: "#000",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            color: "#000",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--MEMO_REQUESTED": {
        color: "#000",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            color: "#000",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--RE-CHECK": {
        backgroundColor: "#FFFFCC",
        color: "black",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            backgroundColor: "#FFFFCC",
            color: "black",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--HOLD": {
        backgroundColor: "#b6b9d6",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        color: "#000",
        "&:hover": {
            backgroundColor: "#b6b9d6",
            color: "#000",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--MEMO_OUT": {
        backgroundColor: "#CAF7F6",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            backgroundColor: "#CAF7F6",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--LOCATION_SENT": {
        backgroundColor: "#FEBD7D",
        color: "black",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            backgroundColor: "#FEBD7D",
            color: "black",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--SELL": {
        backgroundColor: "#CCFF99",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            backgroundColor: "#CCFF99",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    '& .MuiDataGrid-columnHeader': {
        color: '#fff',
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        backgroundColor: 'grey',
    },
    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
        py: "8px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },
    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
        py: "15px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },
    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
        py: "22px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },

}));

const StyledMenu = styled((props) => (
    <Menu
        elevation={0}
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
        }}
        transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
        }}
        {...props}
    />
))(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: 6,
        marginTop: theme.spacing(1),
        minWidth: 180,
        color:
            theme.palette.mode === 'light'
                ? 'rgb(55, 65, 81)'
                : theme.palette.grey[300],
        boxShadow:
            'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
        '& .MuiMenu-list': {
            padding: '4px 0',
        },
        '& .MuiMenuItem-root': {
            '& .MuiSvgIcon-root': {
                fontSize: 18,
                color: theme.palette.text.secondary,
                marginRight: theme.spacing(1.5),
            },
            '&:active': {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    theme.palette.action.selectedOpacity
                ),
            },
        },
    },
}));

export default function StockExport() {

    const [stockData, setStockData] = useState([]);
    const [stockLoader, setStockLoader] = useState(false);

    const [stoneId, setStoneId] = useState("");
    const [showStoneDetailModal, setShowStoneDetailModal] = useState(false);
    const [successSnackbar, setSuccessSnackbar] = useState(false);
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [noOfSelectedStones, setNoOfSelectedStones] = useState("0");
    const [selectedTotalSize, setSelectedTotalSize] = useState("0");
    const [selectedAvgDiscount, setSelectedAvgDiscount] = useState("0");
    const [selectedavgPrice, setSelectedavgPrice] = useState("0");
    const [selectedtotalPrice, setSelectedtotalPrice] = useState("0");
    const [sortModel, setSortModel] = React.useState([]);
    const [columnNames, setColumnNames] = React.useState([]);
    const [proformaData, setProformaData] = useState([]);
    const [locationTransferData, setLocationTransferData] = useState([]);
    const [location, setLocation] = useState("");
    const [locationOptions, setLocationOptions] = useState([]);
    const [fetchData, setFetchData] = useState(false);

    // eslint-disable-next-line
    const [localPermission, setLocalPermission] = useState(
        Constants.USER_PERMISSION.filter(
            (iPermission) =>
                iPermission.permissionid === Constants.PERMISSIONS.REPORT_STOCK_EXPORT
        )
    );

    const handleSearch = () => {
        setStockLoader(true);
        let type = "TO_BE_SENT";

        StockTallyProxy.GetAllProformaByLocation(type, location.value, (response) => {
            if (response) {
                setProformaData(response);
            } else {
                setStockLoader(false);
                setErrorSnackbar(true);
            }
        })

        StockTallyProxy.GetAllLocationTransferStone(type, location.value, (response) => {
            if (response) {
                setLocationTransferData(response);
            } else {
                setStockLoader(false);
                setErrorSnackbar(true);
            }
        })
    };

    const getAllLocationData = () => {
        LocationProxy.GetAllLocationDetailMini((response) => {
            setAllLocationData(response);
        });
    }

    const setAllLocationData = (response) => {
        let optionsArray = [];
        response.map((data) => {
            let option = { value: data.id, label: data.name };
            optionsArray.push(option);
            if (option.value === Constants.loggedInUser.locationid) {
                setLocation(option);
            }
            return true;
        });
        setLocationOptions(optionsArray);
        setFetchData(true);
    }

    useEffect(() => {
        PageTitle("REPORT_STOCK_EXPORT");
        getAllLocationData();
    }, []);

    useEffect(() => {
        if (fetchData) {
            handleSearch();
        }
    }, [fetchData, location]);

    useEffect(() => {
        let mainStockData = [...proformaData, ...locationTransferData];
        const distinctStatus = Utility.getDistinctOptions(mainStockData, 'status');
        prepareColumns(distinctStatus);
        setStockData(mainStockData);
        loadTotalData(mainStockData);
        setStockLoader(false);
    }, [proformaData, locationTransferData]);

    const handleClose = () => {
        setShowStoneDetailModal(false);
    };

    const handleStoneDetailModal = (params) => {
        setStoneId(params.value);
        setShowStoneDetailModal(true);
    };

    const loadTotalData = (data) => {
        let totalSize = 0;
        let totalPrice = 0;   //ppc*size
        let total = 0;       //rapnetprice*size

        data.forEach((iData) => {
            let pricePerCarat = iData.price
            totalSize += iData.size;
            totalPrice += (iData.size * pricePerCarat);
            total += (iData.size * ((100 * pricePerCarat) / (100 + iData.discount)));

        });
        setNoOfSelectedStones(data.length);
        setSelectedTotalSize(Utility.ConvertNumberWithSpecifiedDigits(totalSize));
        setSelectedtotalPrice(Utility.ConvertNumberWithSpecifiedDigits(totalPrice));
        setSelectedavgPrice(
            totalPrice > 0
                ? Utility.ConvertNumberWithSpecifiedDigits(
                    totalPrice / totalSize
                )
                : Utility.ConvertNumberWithSpecifiedDigits(0)
        );
        setSelectedAvgDiscount(
            totalPrice > 0
                ? Utility.ConvertNumberWithSpecifiedDigits(
                    (100 - (totalPrice / total) * 100) * -1
                )
                : Utility.ConvertNumberWithSpecifiedDigits(0)
        );
    };

    const prepareColumns = (distinctStatus) => {
        let columnNames = [];

        Constants.USER_COLUMN_PERMISSION.forEach(iColPermission => {
            if (columnsData.hasOwnProperty(iColPermission)) {
                const columnData = columnsData[iColPermission];
                if (columnData.field === "status") {
                    columnData.type = "singleSelect";
                    columnData.valueOptions = distinctStatus;
                    columnData.filterOperators = Utility.combinedFilters();
                }
                columnNames.push(columnData);
                if (columnData.field === "total") {
                    columnNames.push({
                        field: "locationfrom",
                        headerName: "STONE LOCATION",
                        headerAlign: "center",
                        align: "center",
                        width: 135,
                        type: "singleSelect",
                        valueOptions: Constants.uniqueFilterValues.location.sort((a, b) => a.seq - b.seq).map(option => option.value),
                        filterOperators: Utility.combinedFilters(),
                        renderCell: renderCellExpand,
                    });
                    columnNames.push({
                        field: "locationto",
                        headerName: "SEND TO LOCATION",
                        headerAlign: "center",
                        align: "center",
                        width: 150,
                        type: "singleSelect",
                        valueOptions: Constants.uniqueFilterValues.location.sort((a, b) => a.seq - b.seq).map(option => option.value),
                        filterOperators: Utility.combinedFilters(),
                        renderCell: renderCellExpand,
                    });
                    columnNames.push({
                        field: "finaldestination",
                        headerName: "FINAL DESTINATION",
                        headerAlign: "center",
                        align: "center",
                        width: 140,
                        type: "singleSelect",
                        valueOptions: Constants.uniqueFilterValues.location.sort((a, b) => a.seq - b.seq).map(option => option.value),
                        filterOperators: Utility.combinedFilters(),
                        renderCell: renderCellExpand,
                    });
                    columnNames.push({
                        field: "export",
                        headerName: "EXPORT",
                        headerAlign: "center",
                        align: "center",
                        renderCell: (params) => params.value === true ? (
                            <CheckIcon color="success" />
                        ) : (
                            <ClearIcon color="error" />
                        ),
                    });
                }
            }
        });
        setColumnNames(columnNames);
    }

    const columnsData = {
        "5dd43313-18b9-41a5-9e07-09d61ad6863c": {
            field: "stoneid",
            headerName: "STONEID",
            pinning: { position: 'left' },
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "number",
            valueFormatter: (params) => params.value,
            cellClassName: 'stickyColumn',
            renderCell: (params) => Utility.NeedToShowStoneDetailModal() ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => handleStoneDetailModal(params)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        "24ba0bf8-4ca1-4726-ad90-19a62c8c72e6": {
            field: "boxid",
            headerName: "BOXID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 80
        },
        "62c37a29-d2ff-4c72-8cb3-07e52a83d484": {
            field: "status",
            headerName: "STATUS",
            headerAlign: "center",
            align: "center",
            width: 100,
            valueGetter: (params) => (params.value === "LOCATION_SENT" ? "IN_TRANSIT" : params.value),
            renderCell: (params) => (params.value === "LOCATION_SENT" ? "IN_TRANSIT" : params.value),
            cellClassName: (params) => `super-app-theme--${params.row.status.toUpperCase()}`,
        },
        "54fbd914-6ce7-44a8-9354-db6125ba1992": {
            field: "tag",
            headerName: "TAG",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: (params) => (!params.value) ? "" : params.value,
        },
        "eb5db767-47d2-4444-9b52-7dc35c853fc7": {
            field: "shape",
            headerName: "SHAPE",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.shape.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "9f0b7352-8ca1-459d-944c-eb46dad5aed1": {
            field: "size",
            headerName: "SIZE",
            headerAlign: "center",
            width: 40,
            align: "right",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.size)
        },
        "fe7c00f1-774a-45a2-aec0-e5fb2ea1101e": {
            field: "color",
            headerName: "COLOR",
            headerAlign: "center",
            align: "center",
            width: 65,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.color.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "1035d1d0-bc7c-404f-8f58-c80bea2de613": {
            field: "clarity",
            headerName: "CLARITY",
            headerAlign: "center",
            align: "center",
            width: 75,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.clarity.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "3597803f-8932-40c6-8de6-81006f4d6d5b": {
            field: "cut",
            headerName: "CUT",
            headerAlign: "center",
            align: "center",
            width: 50,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.cut.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "859c71c9-c9c8-4f2c-9eb7-08e388e505ad": {
            field: "pol",
            headerName: "POL",
            headerAlign: "center",
            align: "center",
            width: 40,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.pol.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "5541c182-21b8-496e-b342-b0b89f3d302f": {
            field: "sym",
            headerName: "SYM",
            headerAlign: "center",
            align: "center",
            width: 40,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.sym.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "8296e627-0be5-4a66-830f-ea6867cea137": {
            field: "flo",
            headerName: "FLO",
            headerAlign: "center",
            align: "center",
            width: 60,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.flo.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "798a8663-0f0c-41be-8512-16dd60d5f25e": {
            field: "discount",
            headerName: "DIS",
            headerAlign: "center",
            align: "right",
            width: 50,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.discount)
        },
        "53c8a138-3f8e-486c-a9ba-f5c671065ad7": {
            field: "rapnetprice",
            headerName: "RAP",
            headerAlign: "center",
            align: "right",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.rapnetprice)
        },
        "39c634f1-2644-4307-acbb-983b4de4229e": {
            field: "price",
            headerName: "PPC",
            headerAlign: "center",
            align: "right",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price)
        },
        "7df28e0e-0303-4ff8-a432-9dedb384afa8": {
            field: "total",
            headerName: "TOTAL",
            headerAlign: "center",
            align: "right",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.total)
        },
        "8eaa8a03-d163-436d-8912-51ea06306f44": {
            field: "certificateid",
            headerName: "CERT",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: renderCellExpand,
        },
        "564cee34-901f-4bf4-b7fc-f9f965771d8c": {
            field: "fancycolor1",
            headerName: "F.COL 1",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "7e1a0c81-4c41-4cda-bfff-8e2e81d75ce1": {
            field: "fancycolor2",
            headerName: "F.COL 2",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "ccba9873-174e-4060-963a-9893c3625bb2": {
            field: "fancycolorovertone",
            headerName: "OVERTONE",
            headerAlign: "center",
            align: "center",
            width: 120,
            renderCell: renderCellExpand,
        },
        "c9aa6ee7-30e3-423e-a534-80bb5ef7bbaf": {
            field: "fancycolorintensity",
            headerName: "F.COL.INTENSITY",
            headerAlign: "center",
            align: "center",
            width: 140,
            renderCell: renderCellExpand,
        },
    };

    const rows = stockData;
    const columns = columnNames;

    const allowed = Utility.isReadAllowed(localPermission);

    // Color Scheme Drop Down Button
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleCloseButton = () => {
        setAnchorEl(null);
    };

    return (
        !allowed ? <NoPermission /> :
            <div>
                <Container maxWidth="xxl">
                    {showStoneDetailModal ? (
                        <StoneDetailModal
                            show={showStoneDetailModal}
                            stoneId={stoneId}
                            handleClose={handleClose}
                        />
                    ) : null}
                    <TabMenuSnackbar
                        show={successSnackbar}
                        message={'Success'}
                        variant={"success"}
                        autoHide={Constants.SnackBarCloseTimer}
                        title={"Success"}
                        handleClose={() => setSuccessSnackbar(false)}
                    />
                    <TabMenuSnackbar
                        show={errorSnackbar}
                        message={'Error'}
                        variant={"error"}
                        autoHide={Constants.SnackBarCloseTimer}
                        title={"Error"}
                        handleClose={() => setErrorSnackbar(false)}
                    />
                    <Box m={1} display={"flex"} flexDirection={"row"} justifyContent={"space-between"} alignItems={"center"}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                width: "16%",
                                gap: "10px",
                            }}
                        >
                            <Box>
                                <Typography>Location</Typography>
                            </Box>
                            <Box
                                sx={{
                                    width: "100%",
                                    "& .MuiSelect-select": {
                                        height: "0.5em !important"
                                    }
                                }}
                            >
                                <Select menuPlacement="auto"
                                    blurInputOnSelect={false}
                                    className="basic-single"
                                    classNamePrefix="select"
                                    value={location}
                                    isSearchable={true}
                                    name="status"
                                    menuPortalTarget={document.body}
                                    styles={Constants.Styles}
                                    options={locationOptions}
                                    onChange={(selectedOption) => setLocation(selectedOption)}
                                />
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                m: "1px 15px 0 0",
                                color: "black",
                                padding: "8px 16px",
                                fontWeight: 'normal',
                                fontSize: Constants.defaultTableFontSize,
                                textAlign: "center"
                            }}
                        ><p style={{ margin: "0px" }}>
                                <span>No of Stones: </span> <b>{noOfSelectedStones}</b> &nbsp;&nbsp;|&nbsp;&nbsp;
                                <span>Total Weight: </span> <b>{selectedTotalSize}</b>  &nbsp;&nbsp;|&nbsp;&nbsp;
                                <span>Total Price: </span> <b>{selectedtotalPrice}</b>  &nbsp;&nbsp;|&nbsp;&nbsp;
                                <span>Avg Price: </span> <b>{selectedavgPrice}</b>  &nbsp;&nbsp;|&nbsp;&nbsp;
                                <span>Avg Discount: </span> <b>{selectedAvgDiscount}</b></p>
                        </Box>

                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                            <div>
                                <Box
                                    id="demo-customized-button"
                                    aria-controls={open ? 'demo-customized-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={open ? 'true' : undefined}
                                    variant="outlined"
                                    disableElevation
                                    onClick={handleClick}
                                    endIcon={<KeyboardArrowDownIcon />}
                                    sx={{
                                        color: '#454545',
                                        borderColor: '#454545',
                                        borderRadius: "4px",
                                        border: "1px solid black",
                                        padding: "5px 0 5px 5px",
                                        cursor: "pointer"
                                    }}
                                >
                                    <img
                                        src={colourchart}
                                        alt="Color Scheme"
                                        style={{
                                            height: '24px',
                                            width: '24px',
                                        }}
                                    />
                                    <KeyboardArrowDownIcon />
                                </Box>
                                <StyledMenu
                                    id="demo-customized-menu"
                                    MenuListProps={{
                                        'aria-labelledby': 'demo-customized-button',
                                        style: {
                                            maxHeight: 250
                                        },
                                    }}
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={handleCloseButton}
                                >
                                    <MenuItem onClick={handleCloseButton} disableRipple>
                                        <span style={{ height: "15px", width: "15px", backgroundColor: "#FFFFCC", borderRadius: 20, margin: 10 }}></span>RE-CHECK
                                    </MenuItem>
                                    <MenuItem onClick={handleCloseButton} disableRipple>
                                        <span style={{ height: "15px", width: "15px", backgroundColor: "#000000", borderRadius: 20, margin: 10 }}></span>AVAILABLE
                                    </MenuItem>
                                    <MenuItem onClick={handleCloseButton} disableRipple>
                                        <span style={{ height: "15px", width: "15px", backgroundColor: "#B6B9D6", borderRadius: 20, margin: 10 }}></span>HOLD
                                    </MenuItem>
                                    <MenuItem onClick={handleCloseButton} disableRipple>
                                        <span style={{ height: "15px", width: "15px", backgroundColor: "#CAF7F6", borderRadius: 20, margin: 10 }}></span>MEMO_OUT
                                    </MenuItem>
                                    <MenuItem onClick={handleCloseButton} disableRipple>
                                        <span style={{ height: "15px", width: "15px", backgroundColor: "#CCFF99", borderRadius: 20, margin: 10 }}></span>SELL
                                    </MenuItem>
                                    <MenuItem onClick={handleCloseButton} disableRipple>
                                        <span style={{ height: "15px", width: "15px", backgroundColor: "#FEBD7D", borderRadius: 20, margin: 10 }}></span>IN_TRANSIT
                                    </MenuItem>
                                </StyledMenu>
                            </div>
                        </div>
                    </Box>
                    <Box
                        sx={{
                            backgroundColor: '#fff',
                            color: '#000',
                            width: "100%",
                            position: "relative",
                            "& .MuiTablePagination-selectLabel": {
                                marginTop: "15px",
                            },
                            "& .MuiTablePagination-displayedRows": {
                                marginTop: "15px",
                            },
                            "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            "& .css-cc8tf1": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            height: '80vh'
                        }}
                    >
                        <StyledDataGrid
                            disableRowSelectionOnClick={true}
                            localeText={{ noRowsLabel: "No Record Found" }}
                            sx={{ height: "80vh", fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight, }}
                            slots={{
                                toolbar: GridToolbar,
                            }}
                            rows={rows}
                            style={{}}
                            sortModel={sortModel}
                            onSortModelChange={(newModel) => setSortModel(newModel)}
                            density="compact"
                            columns={columns}
                            getRowId={(row) => row.stoneid}
                            loading={stockLoader}
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        // Hide columns id, the other columns will remain visible
                                        locationid: false,
                                        kapanid: false,
                                        country: false,
                                        quality: false,
                                        shade: false,
                                        repacode: false,
                                        createdat: false,
                                        height: false,
                                        width: false,
                                        packetid: false,
                                        crownheight: false,
                                        crownangle: false,
                                        culetsize: false,
                                        culetcondition: false,
                                        depthpercent: false,
                                        gridlemin: false,
                                        intensitylongtitle: false,
                                        laserinscription: false,
                                        note: false,
                                        paviliondepth: false,
                                        open: false,
                                        gridlecondition: false,
                                        gridlepercent: false,
                                        pavilionangle: false,
                                        white: false,
                                    },
                                },
                                pagination: {
                                    paginationModel: Constants.pageSize,
                                },
                            }}
                            pageSizeOptions={Constants.pageSizeOptions}
                            pagination
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: {
                                        debounceMs: 500,
                                    },
                                },
                            }}
                        />
                        <Box
                            style={{
                                position: "absolute",
                                top: 8,
                                right: "265px",
                                display: "flex",
                                alignItems: "center"
                            }}
                        >
                            {stockLoader ? (
                                <CircularProgress size="1.5rem" />
                            ) : (
                                <Tooltip title="Reload List">
                                    <RefreshOutlinedIcon
                                        onClick={() => handleSearch()}
                                        color="primary"
                                        fontSize="large"
                                        sx={{ marginLeft: "7px", cursor: "pointer", height: "1.7rem", width: "1.7rem" }}
                                    />
                                </Tooltip>
                            )}

                        </Box>
                    </Box>
                </Container>
            </div>
    );
}